import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { convertOdds } from '../utils/oddsConverter'
import { SPORT_DISPLAY_NAMES } from '../config/sports'
import {
  SPORT_AVAILABLE_MARKETS, LEAGUE_TO_GROUP, VIRTUAL_LEAGUES, isVirtualLeague,
  ODDS_HIERARCHY, leagueLabel, buildMarketTree, orderPeriods, playersAreCompetitors,
  isFuturesLeague, futuresSportId, futuresToGames, marketKeysFor, roundLabel,
} from './oddsTaxonomy'
import { BOOKMAKERS_BY_REGION as BOOKMAKERS, ALL_BOOKMAKERS, BOOKMAKER_LOGOS } from '../config/bookmakers'
import { adjustPrice, isExchange, EXCHANGE_FEE_NOTE } from '../config/exchanges'
import PreviewWall from '../components/PreviewWall'
import SubjectImage from '../components/SubjectImage'
import OddsBetslip, { slipKey } from './OddsBetslip'
import { oppFromOutcome } from './trackFromOdds'
import '../styles/app.css'
import '../styles/app-mobile.css'
import useVisiblePolling from '../hooks/useVisiblePolling'

const GAME_GROUP_THRESHOLD_HOURS = 6
const GAME_GROUP_THRESHOLD_HOURS_BY_SPORT = { MLB: 1.8 }
const groupThresholdFor = (sportId) =>
  GAME_GROUP_THRESHOLD_HOURS_BY_SPORT[sportId] ?? GAME_GROUP_THRESHOLD_HOURS

const groupGamesByMatch = (games, sportId) => {
  const threshold = groupThresholdFor(sportId)
  const groups = []

  games.forEach(game => {
    const baseKey = `${game.game.home_team}__${game.game.away_team}`
    const timeStr = game.game.commence_time
    let timeHours = null

    if (timeStr) {
      try {
        const t = new Date(timeStr.endsWith('Z') ? timeStr : timeStr + 'Z')
        timeHours = t.getTime() / (1000 * 3600)
      } catch {}
    }

    const matched = groups.find(g =>
      g.baseKey === baseKey &&
      (timeHours === null || g.timeHours === null || Math.abs(g.timeHours - timeHours) <= threshold)
    )

    // The API returns ONE ENTRY PER BOOKMAKER, so this entry's game_url is
    // THIS bookmaker's link. Merging has to carry it onto the book or every
    // logo in the group points at whichever entry landed first.
    const withUrl = game.bookmakers.map(bk => ({ ...bk, game_url: bk.game_url ?? game.game?.game_url ?? null }))

    if (matched) {
      withUrl.forEach(bk => matched.bookmakers.push(bk))
    } else {
      groups.push({ baseKey, timeHours, game: game.game, bookmakers: withUrl })
    }
  })

  return groups.map(g => ({ game: g.game, bookmakers: g.bookmakers }))
}

const formatTime = (timeStr) => {
  if (!timeStr) return '—'
  try {
    return new Date(timeStr + 'Z').toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  } catch { return timeStr }
}

// `description` qualifies an outcome whose `name` alone is ambiguous. The
// winning_margin_* markets price TWO BANDS PER TEAM, so the team name repeats
// and only the band ("Little Win 1-39" / "Big Win 40+") separates them —
// without it here the two bands collapse into one row and overwrite each
// other's prices. No other market sends the field, so nothing else changes.
const outcomeKey = (outcome) => outcome.player_name
  ? `${outcome.player_name}_${outcome.name}_${outcome.point ?? ''}`
  : `${outcome.team_name ? outcome.team_name + '_' : ''}${outcome.name}_${outcome.description ?? ''}_${outcome.point ?? ''}`

// The subject a FLAT row is about, for its thumbnail. Same rules as
// flatRowSubject in ODDS1.jsx - keep the two in step:
//   player_name / team_name when the feed sends them (team_name only on an
//   unlined outcome: on a handicap at least one book puts the OPPONENT there),
//   a fixture's own team by name (head to head),
//   otherwise the bare name as subject_name, which SubjectImage resolves by
//   trying the crest then the headshot (Brownlow Winner, First Tryscorer).
const flatRowSubject = (row, game) => {
  const outcome = Object.values(row.sources || {})[0]?.outcome
  if (!outcome) return null
  if (outcome.player_name) return { player_name: outcome.player_name }
  if (outcome.team_name && outcome.point == null) return { team_name: outcome.team_name }
  const name = outcome.name
  if (!name) return null
  if (name === game.game.home_team || name === game.game.away_team) return { team_name: name }
  return { subject_name: name }
}

// Correct-score outcomes carry two dynamic "{player}" fields holding each
// player's set count (the API strips the " score" suffix). Standard outcome
// keys aren't players, so anything else with a numeric value is a player score.
const CS_STD_KEYS = new Set(['name', 'price', 'point', 'team_name', 'player_name',
  // Exchange depth (Kalshi, Polymarket). Both are numbers and neither is a
  // player score: an exchange outcome carries EXACTLY two of them, which is the
  // same shape a correct-score outcome has, so without this it was read as one
  // and labelled "size 1669.3-1218.47" on its own row instead of joining the
  // team's row. `alt_prices` is an array and was never caught by the filter.
  'size', 'liquidity'])
// Only correct-score markets have per-competitor score fields, so the market key
// gates this - a numeric field added to any other market can no longer turn a
// row into a scoreline.
const CORRECT_SCORE_KEYS = new Set(['correct_score', 'correct_score_1st_half',
  'correct_score_2nd_half', 'correct_score_sets'])
const getScoreFields = (outcome, marketKey) =>
  (marketKey != null && !CORRECT_SCORE_KEYS.has(marketKey))
    ? []
    : Object.entries(outcome).filter(([k, v]) => !CS_STD_KEYS.has(k) && typeof v === 'number')

// Exchange liquidity, for the cell subtitle: dollars resting at the displayed
// price. Bookmakers never send it, so this returns null for them and the cell
// renders exactly as before. Compact ($9.3k) to fit under a price.
const formatLiquidity = (value) => {
  if (typeof value !== 'number' || !isFinite(value) || value <= 0) return null
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}m`
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`
  return `$${Math.round(value)}`
}

// Build a "Winner scoreline" label for a correct-score outcome, e.g.
// "Alejandro Tabilo 3-0". Falls back to the bare scoreline name if the score
// fields aren't present (e.g. non-tennis / malformed data).
const correctScoreLabel = (outcome) => {
  const scores = getScoreFields(outcome)
  if (scores.length !== 2) return outcome.name
  const [a, b] = scores
  if (a[1] === b[1]) return `Draw ${a[1]}-${b[1]}`
  const [winner, wScore, lScore] = a[1] > b[1] ? [a[0], a[1], b[1]] : [b[0], b[1], a[1]]
  return `${winner} ${wScore}-${lScore}`
}

// MMA outcome order. Method of Victory / Round Betting rows are "<Fighter> —
// <method or round>" and How Fight Ends rows are the bare method, so without
// this they came out in whichever order the first book listed them. Compared
// on the part after the fighter; only applies when BOTH rows are in the list,
// so no other market's order changes except Yes/No markets, which now read
// Yes first (they were alphabetical: No, Yes).
const MMA_OUTCOME_ORDER = ['KO/TKO', 'Submission', 'Round 1', 'Round 2', 'Round 3',
  'Round 4', 'Round 5', 'Decision', 'Draw', 'Yes', 'No']
const mmaOrder = (label) => MMA_OUTCOME_ORDER.indexOf(label.split(' — ').pop())

const findAllBestBookies = (game, sortKey, bestPrice, selectedBookmakers, marketKeys) => {
  const found = []
  for (const bk of game.bookmakers) {
    if (!selectedBookmakers.has(bk.name)) continue
    for (const market of bk.markets) {
      if (!marketKeys.includes(market.key)) continue
      for (const outcome of market.outcomes) {
        if (outcomeKey(outcome) === sortKey && adjustPrice(outcome.price, bk.name) === bestPrice) found.push(bk.name)
      }
    }
  }
  return found
}

const getBestOdds = (games, marketKeys) => {
  const best = {}
  games.forEach(game => {
    const gameId = `${game.game.home_team}_${game.game.away_team}_${game.game.commence_time}`
    game.bookmakers.forEach(bk => {
      bk.markets.filter(m => marketKeys.includes(m.key)).forEach(market => {
        market.outcomes.forEach(outcome => {
          const key = `${gameId}_${outcomeKey(outcome)}`
          // Same adjustment as the cell, or the best-odds column would
          // disagree with the row it is summarising.
          const p = adjustPrice(outcome.price, bk.name)
          if (!best[key] || p > best[key]) best[key] = p
        })
      })
    })
  })
  return best
}

// ─── FilterDropdown — uses position:fixed so it doesn't overflow screen ───────

const FilterDropdown = ({ isOpen, onToggle, buttonContent, children, isActive }) => (
  <div style={{ position: 'relative', minWidth: 0, flex: 1 }} onClick={e => e.stopPropagation()}>
    <button
      onClick={onToggle}
      className="bc-btn"
      style={{ width: '100%', justifyContent: 'space-between', overflow: 'hidden' }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{buttonContent}</span>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7, flexShrink: 0 }}>
        {isOpen ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
      </svg>
    </button>
    {isOpen && (
      <div className="odds-m-dd-panel">
        {children}
      </div>
    )}
  </div>
)

const ChevronRight = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.45, flexShrink: 0 }}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

const CheckboxItem = ({ item, selected, onToggle, logo }) => (
  <div onClick={() => onToggle(item)} className={`odds-m-check${selected ? ' checked' : ''}`}>
    {logo && <img src={logo} alt={item} style={{ width: '20px', height: '20px', borderRadius: '4px', flexShrink: 0 }} />}
    <span>{item}</span>
  </div>
)

// ─── Component ────────────────────────────────────────────────────────────────

const DEFAULT_SPORT  = 'NBA'
const DEFAULT_MARKET = 'Head To Head'

// Flat one-player-per-row outright ladders: one row per player, one price, no
// Over/Under pairing and no line to sort on. They DEFAULT to shortest price
// first — the way every book presents an outright, favourite at the top — and
// the Best column cycles shortest → longest → A-Z from there.
//
// AFL futures get the same order without being listed here: a futures row has
// no point, team or player name to sort on, so getOutcomeRows sorts it on
// game.futuresTitle before reaching any of these branches.
const ODDS_SORTABLE_MARKETS = new Set([
  'First Tryscorer',
  'First or Second Tryscorer',
  'First, Second or Third Tryscorer',
  'First Touchdown Scorer',
])

export default function ODDS1Mobile({ isVisible, isPreview }) {
  const { oddsFormat, hasAccess, hadTrial, loading: authLoading } = useAuth()
  isPreview = isPreview || authLoading || !hasAccess('odds')

  const tableWrapperRef = useRef(null)
  const [tableHeight, setTableHeight] = useState(400)

  const [selectedSport, setSelectedSport] = useState(DEFAULT_SPORT)
  const [selectedMarketLabel, setSelectedMarketLabel] = useState(DEFAULT_MARKET)

  // The selector is a DRILL-DOWN on mobile, not two side-by-side columns: at
  // 38% of a phone's width the sport column showed 41 rows of truncated names.
  // One full-width list at a time, with a back arrow and a breadcrumb whose
  // crumbs are tappable, so going back a step never costs more than one tap.
  const [step, setStep] = useState('sport')
  const [draftCategory, setDraftCategory] = useState(null)
  const [draftGroup, setDraftGroup] = useState(null)
  const [draftBaseKey, setDraftBaseKey] = useState(null)

  // Per virtual league, the members that last returned games — see ODDS1.jsx.
  const liveMembersRef = useRef({})
  const [dropdownOpen, setDropdownOpen] = useState(false)
  // Best-odds column sort (tryscorer markets only): null = default, 'desc' / 'asc' = by best price.
  const [bestSort, setBestSort] = useState(null)
  const [bookmakerModalOpen, setBookmakerModalOpen] = useState(false)
  const [expandedBestKey, setExpandedBestKey] = useState(null)
  const [games, setGames] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [selectedBookmakers, setSelectedBookmakers] = useState(new Set(ALL_BOOKMAKERS))

  // Selections waiting in the betslip. A price is added by tapping it and
  // removed by tapping again, so the table shows what is in the slip and no
  // dialog interrupts the comparing this page exists for.
  //
  // Declared AFTER selectedSport: the callbacks below list it as a dependency,
  // and a dependency array is evaluated during render, so sitting above it
  // throws on the temporal dead zone.
  const [slipLegs, setSlipLegs] = useState([])
  const [slipToast, setSlipToast] = useState(null)

  const slipKeys = useMemo(() => new Set(slipLegs.map(slipKey)), [slipLegs])

  const removeFromSlip = useCallback((key) => {
    setSlipLegs(prev => prev.filter(leg => slipKey(leg) !== key))
  }, [])

  // Held as the raw string while typing so a half-entered "1." is not coerced
  // to a number and the caret thrown to the end.
  const setLegOdds = useCallback((key, value) => {
    setSlipLegs(prev => prev.map(leg => (slipKey(leg) === key ? { ...leg, odds: value } : leg)))
  }, [])

  /** One cell as an opportunity. The single place a cell becomes a bet. */
  const oppForCell = useCallback((game, source, bookmaker, price) => oppFromOutcome({
    outcome:   source.outcome,
    marketKey: source.marketKey,
    game,
    bookmaker,
    price,
    sport:     selectedSport,
    gameUrl:   game?.bookmakers?.find(b => b.name === bookmaker)?.game_url ?? null,
  }), [selectedSport])

  /** That cell's slip key, or null when there is nothing trackable in it. */
  const cellSlipKey = useCallback((game, source, bookmaker, price) => {
    const opp = oppForCell(game, source, bookmaker, price)
    return opp ? slipKey(opp) : null
  }, [oppForCell])

  /** Tap a price to put it in the slip, tap again to take it out. */
  const toggleOutcome = (game, row, bookmaker, price) => {
    const source = row?.sources?.[bookmaker]
    if (isPreview || !source) return
    const opp = oppForCell(game, source, bookmaker, price ?? row.prices?.[bookmaker])
    if (!opp) return

    const key = slipKey(opp)
    setSlipLegs(prev => prev.some(leg => slipKey(leg) === key)
      ? prev.filter(leg => slipKey(leg) !== key)
      : [...prev, opp])
  }
  const [expandedPlayers, setExpandedPlayers] = useState(new Set())
  const [hiddenGames, setHiddenGames] = useState(new Set())
  const [gamesModalOpen, setGamesModalOpen] = useState(false)
  const [tournamentsModalOpen, setTournamentsModalOpen] = useState(false)
  const [hiddenTournaments, setHiddenTournaments] = useState(new Set())

  const toggleTournament = (name) => setHiddenTournaments(prev => {
    const next = new Set(prev)
    next.has(name) ? next.delete(name) : next.add(name)
    return next
  })
  const [hiddenPlayers, setHiddenPlayers] = useState(new Set())
  const [playersModalOpen, setPlayersModalOpen] = useState(false)
  // Brownlow 3 Votes: round showing (null -> earliest with prices) and its picker.
  const [selectedRound, setSelectedRound] = useState(null)
  const [roundModalOpen, setRoundModalOpen] = useState(false)
  const [playerSearch, setPlayerSearch] = useState('')
  const [gameSearch, setGameSearch] = useState('')

  const selectedMarketKeys = selectedMarketLabel
    ? marketKeysFor(selectedMarketLabel)
    : []

  // Only the tryscorer ladder markets get the clickable best-odds sort.
  const oddsSortable = ODDS_SORTABLE_MARKETS.has(selectedMarketLabel)

  useEffect(() => {
    if (!isVisible) return
    const keys = marketKeysFor(DEFAULT_MARKET)
    fetchOdds(DEFAULT_SPORT, keys)
    // fetchOdds is a stable useCallback; intent is to fetch once when the page becomes visible
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible])

  useVisiblePolling(
    () => fetchOdds(selectedSport, selectedMarketKeys),
    30000,
    autoRefresh && !!selectedSport && selectedMarketKeys.length > 0 && isVisible
  )

  // Mirrors ODDS1.jsx. Every game comes back tagged with its tournament, so a
  // multi-tournament sport (Challenger) and a fanned-out virtual league (ATP)
  // look the same to everything downstream.
  const fetchOneSport = async (sportId, marketKeys) => {
    // Futures has no fixture, so it has its own endpoint and its own proxy. The
    // API is asked for the REAL sport (AFL) — AFL_FUTURES is a dropdown id that
    // exists only in this app — and the market-major response is un-pivoted
    // into the games[] shape the table already renders.
    if (isFuturesLeague(sportId)) {
      const { data, error: fnError } = await supabase.functions.invoke('futures-proxy', {
        body: { sport_id: futuresSportId(sportId), market_type: marketKeys }
      })
      if (fnError) throw fnError
      // The REAL sport id ("AFL"), not the AFL_FUTURES dropdown id - the image
      // lookup resolves its folder from it. Must match the desktop call.
      return futuresToGames(data, futuresSportId(sportId))
    }

    const { data, error: fnError } = await supabase.functions.invoke('odds-proxy', {
      body: { sport_id: sportId, market_type: marketKeys }
    })
    if (fnError) throw fnError
    return (data?.games || []).map(g => ({
      ...g,
      // sport is the MEMBER league (EPL), never the virtual id (Soccer) that
      // fanned out to it - crests and headshots live in per-league folders, so
      // WITHOUT THIS every image on this page resolves to null and draws a
      // placeholder. ODDS1.jsx has always stamped it; mobile did not, which is
      // why mobile showed no photos at all.
      game: { ...g.game, sport: sportId, tournament: g.game?.tournament || SPORT_DISPLAY_NAMES[sportId] || sportId },
    }))
  }

  const fetchOdds = useCallback(async (sportId, marketKeys) => {
    try {
      setLoading(true)
      setError(null)

      if (isVirtualLeague(sportId)) {
        // Out-of-season members return empty and are not charged, so the cost of
        // asking all of them is round trips, not credits. Auto-refresh then
        // narrows to whichever ones actually had games.
        const all = VIRTUAL_LEAGUES[sportId].members
        const live = liveMembersRef.current[sportId]
        const targets = (live && live.length > 0) ? live : all

        const results = await Promise.allSettled(targets.map(id => fetchOneSport(id, marketKeys)))
        const merged = []
        const nowLive = []
        results.forEach((r, i) => {
          if (r.status !== 'fulfilled' || r.value.length === 0) return
          nowLive.push(targets[i])
          merged.push(...r.value)
        })
        if (merged.length === 0 && results.every(r => r.status === 'rejected')) {
          throw new Error(results[0]?.reason?.message || 'Failed to fetch odds')
        }
        liveMembersRef.current[sportId] = nowLive.length > 0 ? nowLive : null
        setGames(merged)
      } else {
        setGames(await fetchOneSport(sportId, marketKeys))
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch odds')
      setGames([])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSportSelect = (sportId) => {
    setSelectedSport(sportId)
    setHiddenTournaments(new Set())
    setSelectedMarketLabel(null)
    setGames([])
    setError(null)
  }

  const handleMarketSelect = (label) => {
    setStep('sport')
    setSelectedMarketLabel(label)
    const keys = marketKeysFor(label)
    setHiddenGames(new Set())
    setHiddenPlayers(new Set()); setPlayerSearch('')
    setGameSearch('')
    fetchOdds(selectedSport, keys)
    setDropdownOpen(false)
  }

  // Same as ODDS1.jsx: rounds present in the data (only Brownlow 3 Votes carries
  // futuresRound), and the round actually shown.
  const availableRounds = useMemo(() => (
    [...new Set(games.map(g => g.game.futuresRound).filter(r => r != null))].sort((a, b) => a - b)
  ), [games])
  const effectiveRound = availableRounds.includes(selectedRound) ? selectedRound : (availableRounds[0] ?? null)

  const groupedGames = useMemo(() => {
    // Filter to ONE round BEFORE grouping: these blocks have no start time, so
    // groupGamesByMatch merges on team names alone, and 55 pairings meet twice
    // in a season - both rounds going in would merge them with the wrong prices.
    const roundGames = effectiveRound == null
      ? games
      : games.filter(g => g.game.futuresRound == null || g.game.futuresRound === effectiveRound)
    const grouped = groupGamesByMatch(roundGames, selectedSport)
    return grouped.sort((a, b) => {
      const tA = a.game.commence_time ? new Date(a.game.commence_time.endsWith('Z') ? a.game.commence_time : a.game.commence_time + 'Z').getTime() : 0
      const tB = b.game.commence_time ? new Date(b.game.commence_time.endsWith('Z') ? b.game.commence_time : b.game.commence_time + 'Z').getTime() : 0
      return tA - tB
    })
  }, [games, selectedSport, effectiveRound])

  useEffect(() => {
    const calcHeight = () => {
      if (tableWrapperRef.current) {
        const top = tableWrapperRef.current.getBoundingClientRect().top
        setTableHeight(window.innerHeight - top - 8)
      }
    }
    const timer = setTimeout(calcHeight, 50)
    window.addEventListener('resize', calcHeight)
    return () => { clearTimeout(timer); window.removeEventListener('resize', calcHeight) }
  }, [isVisible, groupedGames.length])

  const allBookmakers = useMemo(() => {
    const bkSet = new Set()
    groupedGames.forEach(g => g.bookmakers.forEach(bk => bkSet.add(bk.name)))
    return Array.from(bkSet).sort()
  }, [groupedGames])

  const visibleBookmakers = allBookmakers.filter(bk => selectedBookmakers.has(bk))

  const toggleBookmakerSelection = (bk) => {
    setSelectedBookmakers(prev => {
      const next = new Set(prev)
      next.has(bk) ? next.delete(bk) : next.add(bk)
      return next
    })
  }

  const toggleCountryBookmakers = (country) => {
    const countryBks = BOOKMAKERS[country]
    const allSelected = countryBks.every(bk => selectedBookmakers.has(bk))
    setSelectedBookmakers(prev => {
      const next = new Set(prev)
      countryBks.forEach(bk => allSelected ? next.delete(bk) : next.add(bk))
      return next
    })
  }

  const gameKey = (g) => `${g.game.home_team}_${g.game.away_team}_${g.game.commence_time}`
  const gameLabel = (g) => g.game.futuresTitle || `${g.game.away_team} vs ${g.game.home_team}`

  // Distinct tournaments in what is loaded — see ODDS1.jsx. Only ever more than
  // one for Challenger or a fanned-out virtual league.
  const allTournaments = useMemo(() => {
    const set = new Set()
    groupedGames.forEach(g => { if (g.game?.tournament) set.add(g.game.tournament) })
    return [...set].sort()
  }, [groupedGames])

  const visibleGames = groupedGames.filter(g =>
    !hiddenGames.has(gameKey(g)) && !hiddenTournaments.has(g.game?.tournament)
  )
  const toggleGame = (key) => setHiddenGames(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next
  })

  const allPlayers = useMemo(() => {
    const set = new Set()
    groupedGames.forEach(g => g.bookmakers.forEach(bk =>
      bk.markets.filter(m => selectedMarketKeys.includes(m.key)).forEach(m =>
        m.outcomes.forEach(o => { if (o.player_name) set.add(o.player_name) })
      )
    ))
    return Array.from(set).sort()
    // selectedMarketKeys derives from selectedMarketLabel, which is keyed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedGames, selectedMarketLabel])

  // Set form of the above, so the player band can be told apart from a
  // team-total or game-total band in O(1). The band's label is just a parsed
  // prefix - a player name, a team name or a market name - and only this can
  // say which. Mirrors playerNameSet in ODDS1.jsx.
  const playerNameSet = useMemo(() => new Set(allPlayers), [allPlayers])

  const togglePlayerFilter = (name) => setHiddenPlayers(prev => {
    const next = new Set(prev); next.has(name) ? next.delete(name) : next.add(name); return next
  })

  const bestOddsMap = useMemo(() => {
    if (!selectedMarketKeys.length || !groupedGames.length) return {}
    return getBestOdds(groupedGames, selectedMarketKeys)
    // selectedMarketKeys derives from selectedMarketLabel, which is keyed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedGames, selectedMarketLabel])

  const getOutcomeRows = (game) => {
    const outcomeMap = {}
    game.bookmakers.forEach(bk => {
      if (!selectedBookmakers.has(bk.name)) return
      bk.markets.filter(m => selectedMarketKeys.includes(m.key)).forEach(market => {
        market.outcomes.forEach(outcome => {
          if (outcome.player_name && hiddenPlayers.has(outcome.player_name)) return
          const isCorrectScore = getScoreFields(outcome, market.key).length === 2
          const label = isCorrectScore
            ? correctScoreLabel(outcome)
            // Yes/No at a round number. The generic point branch below would
            // read "Yes +2", so the round is named instead.
            : market.key === 'fight_to_start_round'
            ? `Start Round ${outcome.point} — ${outcome.name}`
            : outcome.player_name
            ? `${outcome.player_name} — ${outcome.name}${outcome.point != null ? ` ${Math.abs(outcome.point)}` : ''}`
            : outcome.point != null
              ? `${outcome.team_name ? outcome.team_name + ' — ' : ''}${outcome.name} ${outcome.point > 0 && outcome.name !== 'Over' && outcome.name !== 'Under' ? '+' : ''}${outcome.point}`
              : outcome.description
                ? `${outcome.name} — ${outcome.description}`
                : outcome.name
          const sortKey = outcomeKey(outcome)
          if (!outcomeMap[sortKey]) {
            outcomeMap[sortKey] = { label, prices: {}, sources: {}, sortKey }
            if (isCorrectScore) {
              const scores = getScoreFields(outcome)
              const [a, b] = scores
              const draw = a[1] === b[1]
              const [winner, wScore, lScore] = draw
                ? ['Draw', a[1], b[1]]
                : a[1] > b[1] ? [a[0], a[1], b[1]] : [b[0], b[1], a[1]]
              outcomeMap[sortKey].cs = { winner, wScore, lScore, draw }
            }
          }
          // Price AND market key, per BOOKMAKER.
          //
          // A row is one proposition, but the market behind it differs by book:
          // Bet365 may price "Over 0.5 hits" as batter_hits while BetRight lists
          // the same bet as a 1+ milestone. They collide on one row because some
          // feeds name milestone outcomes "Over" — correctly, since the two are
          // the same wager — but each book's own key is what its cell should
          // report.
          //
          // Recorded in step with the price, so the key always belongs to the
          // number on screen. Settlement is indifferent (both strip to the same
          // base_market_type, point and side); this is about the bet naming the
          // market the user will actually find at that book.
          // Exchange prices are fee-adjusted HERE, at the single point where a
          // price enters the row, so every consumer downstream - best-odds,
          // highlighting, sorting, the bet slip - compares like with like
          // without knowing anything about exchanges. Market-type agnostic:
          // the fee is a property of the contract, so it applies to h2h,
          // spreads, totals and player props alike. See config/exchanges.js.
          const cellPrice = adjustPrice(outcome.price, bk.name)
          const existing = outcomeMap[sortKey].prices[bk.name]
          if (!existing || cellPrice > existing) {
            outcomeMap[sortKey].prices[bk.name] = cellPrice
            outcomeMap[sortKey].sources[bk.name] = { outcome, marketKey: market.key }
          }
        })
      })
    })
    const rows = Object.values(outcomeMap)

    // Futures: SHORTEST PRICE FIRST, the way every book presents an outright —
    // favourite at the top, roughies at the bottom.
    //
    // Returned before the branches below because a futures row has no point, no
    // team and no player name for any of them to sort on, so it would fall
    // through to A-Z and scatter the 501.00 no-hopers among the favourites.
    //
    // Ordered on the BEST price across books, which is the number the page
    // highlights, so the order matches the column a reader actually scans.
    // Equal prices fall back to A-Z rather than whatever order the books came
    // back in, so the list does not reshuffle between refreshes.
    if (game.game.futuresTitle) {
      const best = (row) => {
        const prices = Object.values(row.prices)
        return prices.length ? Math.max(...prices) : Infinity
      }
      return rows.sort((a, b) => best(a) - best(b) || a.label.localeCompare(b.label))
    }

    const getPoint = (row) => {
      const m = row.sortKey.match(/_(-?\d+\.?\d*)$/)
      return m ? parseFloat(m[1]) : null
    }
    const getTeam = (row) => row.sortKey.replace(/_-?\d+\.?\d*$/, '')

    const isSpread = rows.some(r => (getPoint(r) ?? 0) < 0)

    if (isSpread) {
      const negRows = rows.filter(r => (getPoint(r) ?? 0) < 0)
        .sort((a, b) => Math.abs(getPoint(a)) - Math.abs(getPoint(b)) || a.label.localeCompare(b.label))
      const posRows = rows.filter(r => (getPoint(r) ?? 0) > 0)
      const noPointRows = rows.filter(r => getPoint(r) === null)
      const usedKeys = new Set()
      const paired = []
      negRows.forEach(neg => {
        paired.push(neg)
        const mirror = posRows.find(p =>
          !usedKeys.has(p.sortKey) &&
          Math.abs(getPoint(p)) === Math.abs(getPoint(neg)) &&
          getTeam(p) !== getTeam(neg)
        )
        if (mirror) { usedKeys.add(mirror.sortKey); paired.push(mirror) }
      })
      posRows.filter(p => !usedKeys.has(p.sortKey)).forEach(p => paired.push(p))
      return [...noPointRows, ...paired]
    }

    // Correct score (scoreline): group rows by winner (home first, away next,
    // draws last), then by winning score, then losing score.
    if (rows.length && rows.every(r => r.cs)) {
      const homeTeam = game.game.home_team
      const rank = (cs) => cs.draw ? 2 : cs.winner === homeTeam ? 0 : 1
      rows.sort((a, b) => {
        const ar = rank(a.cs), br = rank(b.cs)
        if (ar !== br) return ar - br
        if (a.cs.winner !== b.cs.winner) return a.cs.winner.localeCompare(b.cs.winner)
        if (a.cs.wScore !== b.cs.wScore) return a.cs.wScore - b.cs.wScore
        return a.cs.lScore - b.cs.lScore
      })
      return rows
    }

    rows.sort((a, b) => {
      const aPoint = getPoint(a)
      const bPoint = getPoint(b)
      // A bare "Draw" beside fighter-prefixed rows (Method of Victory, Round
      // Betting) would otherwise sort FIRST on its empty prefix.
      const prefixed = a.label.includes(' — ') || b.label.includes(' — ')
      const aTeam = a.label.includes(' — ') ? a.label.split(' — ')[0] : (prefixed && a.label === 'Draw' ? '\uffff' : '')
      const bTeam = b.label.includes(' — ') ? b.label.split(' — ')[0] : (prefixed && b.label === 'Draw' ? '\uffff' : '')
      if (aTeam !== bTeam) return aTeam.localeCompare(bTeam)
      const aMma = mmaOrder(a.label)
      const bMma = mmaOrder(b.label)
      if (aMma !== -1 && bMma !== -1 && aMma !== bMma) return aMma - bMma
      if (aPoint !== null && bPoint !== null) {
        if (aPoint !== bPoint) return aPoint - bPoint
        const aOver = a.label.includes('Over')
        const bOver = b.label.includes('Over')
        if (aOver && !bOver) return -1
        if (!aOver && bOver) return 1
        return 0
      }
      const homeTeam = game.game.home_team
      const awayTeam = game.game.away_team
      const rank = (label) => {
        if (label === homeTeam) return 0
        if (label === awayTeam) return 1
        if (label === 'Draw') return 2
        return 3
      }
      const aRank = rank(a.label)
      const bRank = rank(b.label)
      if (aRank !== bRank) return aRank - bRank
      const aPlayer = a.sortKey.split('_')[0]
      const bPlayer = b.sortKey.split('_')[0]
      if (aPlayer !== bPlayer) return aPlayer.localeCompare(bPlayer)
      const aOver = a.label.includes('Over')
      const bOver = b.label.includes('Over')
      if (aOver && !bOver) return -1
      if (!aOver && bOver) return 1
      return 0
    })

    return rows
  }

  // Group Over/Under rows (player props & totals) into collapsible blocks. Returns null
  // unless every row is an Over/Under line and a group has alt lines to hide.
  const getLineGroups = (rows) => {
    // sortKey shapes this has to read, straight out of outcomeKey():
    //   player prop  "Marcus Bontempelli_Over_20.5"   player_Over_point
    //   team total   "Sydney Swans_Over__102.5"       team_Over_<desc>_point
    //   game total   "Over__45.5"                     Over_<desc>_point
    //
    // *** THE NON-PLAYER BRANCH ALWAYS EMITS A DESCRIPTION SEGMENT, *** and for
    // a total that segment is EMPTY - hence the DOUBLE underscore. The old
    // pattern here demanded a digit immediately after "Over_", so every TEAM
    // total failed to parse and fell through to the flat, always-expanded path;
    // only player props ever grouped. Brownlow Club Total Votes is what exposed
    // it, but it was every team total on this page. Desktop fixed this in
    // ODDS1.jsx; mobile kept the old pair, including the pre-test gate, which
    // rejected the same keys one step earlier.
    const parse = (r) => {
      const m = r.sortKey.match(/^(?:(.*?)_)?(Over|Under)_(?:([^_]*)_)?(-?\d+\.?\d*)$/)
      if (!m) return null
      return { group: m[1] || '', side: m[2], point: parseFloat(m[4]) }
    }
    if (!rows.length || rows.some(r => !parse(r))) return null
    const isTotals = rows.every(r => parse(r).group === '')
    const groups = new Map()
    // Use the selected market's own display name for the game-totals band
    // (e.g. "Total Sets", "Total Games") instead of a hardcoded "Total Points".
    const totalsLabel = selectedMarketLabel || 'Total'
    rows.forEach(r => {
      const { group, point } = parse(r)
      const key = isTotals ? totalsLabel : group
      if (!groups.has(key)) groups.set(key, new Map())
      const lines = groups.get(key)
      const pk = String(point)
      if (!lines.has(pk)) lines.set(pk, { point, rows: [] })
      lines.get(pk).rows.push(r)
    })
    const result = Array.from(groups.entries()).map(([label, lineMap]) => {
      const lines = Array.from(lineMap.values()).sort((a, b) => a.point - b.point)
      lines.forEach(l => l.rows.sort((a, b) => (b.label.includes('Over') ? 1 : 0) - (a.label.includes('Over') ? 1 : 0)))
      const bookCount = (line) => { const s = new Set(); line.rows.forEach(r => Object.keys(r.prices).forEach(b => s.add(b))); return s.size }
      const points = lines.map(l => l.point)
      const median = points.length ? points.slice().sort((a, b) => a - b)[Math.floor(points.length / 2)] : 0
      const main = lines.reduce((best, l) => {
        if (!best) return l
        const d = bookCount(l) - bookCount(best)
        if (d !== 0) return d > 0 ? l : best
        return Math.abs(l.point - median) < Math.abs(best.point - median) ? l : best
      }, null)
      return { label, lines, mainPoint: main ? main.point : null }
    }).sort((a, b) => a.label.localeCompare(b.label))
    // Worth grouping if there are alt lines to hide OR more than one group to
    // separate. Team totals are the second case - and Brownlow Club Total Votes
    // is the one that exposed it: each club is priced at a SINGLE line, so there
    // is nothing to collapse, but banding them by club is still what turns a
    // flat list of 36 Overs and Unders into one block per club.
    // Mobile used to test only the first condition and so fell back to flat
    // rows there while desktop banded them. A single group with a single line (a
    // game total) has neither and still stays flat.
    if (result.length <= 1 && !result.some(g => g.lines.length > 1)) return null
    return result
  }

  const getSpreadGroup = (rows) => {
    const getPoint = (r) => { const m = r.sortKey.match(/_(-?\d+\.?\d*)$/); return m ? parseFloat(m[1]) : null }
    const getTeam = (r) => r.sortKey.replace(/_-?\d+\.?\d*$/, '')
    if (!rows.length || rows.some(r => getPoint(r) === null)) return null
    const negRows = rows.filter(r => getPoint(r) < 0)
    const posRows = rows.filter(r => getPoint(r) > 0)
    // Only a real spread/handicap has negative (favourite) lines. Without them
    // (e.g. Over/Under props at 0.5), bail so the flat O/U path handles it.
    if (!negRows.length) return null
    // One entry per PROPOSITION (favourite + opposite-team mirror). Books can
    // price BOTH directions at the same abs, so key by the negative side and
    // keep both rather than collapsing to one.
    const usedPos = new Set()
    const lines = []
    negRows.forEach(neg => {
      const mirror = posRows.find(p =>
        !usedPos.has(p.sortKey) &&
        Math.abs(getPoint(p)) === Math.abs(getPoint(neg)) &&
        getTeam(p) !== getTeam(neg)
      )
      if (mirror) usedPos.add(mirror.sortKey)
      lines.push({ point: Math.abs(getPoint(neg)), rows: mirror ? [neg, mirror] : [neg] })
    })
    posRows.filter(p => !usedPos.has(p.sortKey)).forEach(p => {
      lines.push({ point: Math.abs(getPoint(p)), rows: [p] })
    })
    lines.sort((a, b) => a.point - b.point || a.rows[0].label.localeCompare(b.rows[0].label))
    if (lines.length < 2) return null
    const bookCount = (line) => { const s = new Set(); line.rows.forEach(r => Object.keys(r.prices).forEach(b => s.add(b))); return s.size }
    const main = lines.reduce((best, l) => {
      if (!best) return l
      const d = bookCount(l) - bookCount(best)
      if (d !== 0) return d > 0 ? l : best
      return l.point < best.point ? l : best
    }, null)
    return [{ label: 'Handicap', lines, mainPoint: main ? main.point : null }]
  }

  const togglePlayer = (key) => setExpandedPlayers(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next
  })

  // Renders one mobile odds <tr>. Shared by flat markets and grouped sub-rows.
  // Info bubble on the EXCHANGE filter group: why an exchange price here is
  // smaller than the one on the exchange's own screen.
  //
  // The dropdown it lives in is `overflow-y: auto`, which CLIPS any child that
  // overflows it - so the bubble cannot simply hang outside the panel. It is
  // kept NARROWER than the panel and grows back across it, which keeps the
  // whole thing inside the scroll box at any scroll position. Copy is in
  // config/exchanges.js and is deliberately two lines: this is a filter
  // panel, and the full explanation belongs on the education page.
  const ExchangeFeeInfo = () => {
    const [open, setOpen] = useState(false)
    return (
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
          aria-label={EXCHANGE_FEE_NOTE.title}
          style={{
            width: '14px', height: '14px', borderRadius: '50%', cursor: 'pointer',
            border: '1px solid #475569', backgroundColor: open ? '#475569' : '#334155',
            color: '#94a3b8', fontSize: '9px', fontWeight: 700, lineHeight: 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
          }}
        >i</button>
        {open && (
          <span
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: '19px', left: 0, zIndex: 400, width: '192px',
              padding: '8px 9px', textAlign: 'left', backgroundColor: '#111a2b',
              border: '1px solid #475569', borderRadius: '6px',
              boxShadow: '0 6px 16px rgba(0,0,0,0.5)', cursor: 'default',
            }}
          >
            <span style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, color: '#f1f5f9', marginBottom: '4px' }}>
              {EXCHANGE_FEE_NOTE.title}
            </span>
            <span style={{ display: 'block', fontSize: '9.5px', lineHeight: 1.4, color: '#94a3b8', marginBottom: '6px' }}>
              {EXCHANGE_FEE_NOTE.body}
            </span>
            <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {EXCHANGE_FEE_NOTE.rates.map(r => (
                <span key={r.name} style={{
                  fontSize: '9px', fontWeight: 600, color: '#cbd5e1', padding: '1px 5px',
                  backgroundColor: '#334155', border: '1px solid #475569', borderRadius: '3px',
                }}>{r.name} {r.label}</span>
              ))}
            </span>
          </span>
        )}
      </span>
    )
  }

  const renderOddsRow = (game, gameId, row, { label, blurRow = false, blurFromCol = Infinity, indent = false, showSubject = false } = {}) => {
    const bestPrice = bestOddsMap[`${gameId}_${row.sortKey}`]
    const bestBookies = bestPrice ? findAllBestBookies(game, row.sortKey, bestPrice, selectedBookmakers, selectedMarketKeys) : []
    const [firstBookie, ...extraBookies] = bestBookies
    // The best cell is a price like any other and is often the only one in
    // view on a narrow screen, so it is selectable too — at the book whose
    // logo it shows. Its logo already owns a tap (the +N expander), so only
    // the cell around it toggles the slip.
    const bestSource = firstBookie ? row.sources?.[firstBookie] : null
    const bestTrackable = !!bestPrice && !!bestSource && !blurRow && !isPreview
    const bestInSlip = bestTrackable && slipKeys.has(cellSlipKey(game, bestSource, firstBookie, bestPrice))
    return (
      <tr key={row.sortKey} style={{ filter: blurRow ? 'blur(5px)' : 'none', pointerEvents: blurRow ? 'none' : 'auto', userSelect: blurRow ? 'none' : 'auto' }}>
        <td style={{
          padding: '8px 8px', paddingLeft: indent ? '20px' : '8px', textAlign: 'left', fontSize: '11.5px',
          color: indent ? '#94a3b8' : '#cbd5e1', fontWeight: '500',
          borderBottom: '1px solid #253347', borderRight: '1px solid #334155',
          position: 'sticky', left: 0, backgroundColor: '#010714', zIndex: 3,
        }}>
          {/* Flat rows only (head to head, tryscorers, outrights): no band
              above them to carry the image. 18px so a name still fits the
              narrow sticky column on one line. */}
          {(() => {
            const subj = showSubject ? flatRowSubject(row, game) : null
            if (!subj) return label ?? row.label
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span className="odds-m-row-subject">
                  <SubjectImage opp={{ ...subj, sport: game.game.sport }} />
                </span>
                <span>{label ?? row.label}</span>
              </span>
            )
          })()}
        </td>
        <td
          className={bestTrackable ? 'odds-track-cell' + (bestInSlip ? ' odds-track-cell--on' : '') : undefined}
          onClick={bestTrackable ? () => toggleOutcome(game, row, firstBookie, bestPrice) : undefined}
          style={{
          padding: '8px 3px', textAlign: 'center', fontSize: '11.5px', fontWeight: '700',
          color: '#b0bec9', borderBottom: '1px solid #253347', borderRight: '2px solid #334155',
          cursor: bestTrackable ? 'pointer' : 'default',
        }}>
          {bestPrice ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
              {firstBookie && BOOKMAKER_LOGOS[firstBookie] && (
                <span
                  className="odds-m-logo-wrap"
                  onClick={extraBookies.length > 0 ? (e) => { e.stopPropagation(); setExpandedBestKey(expandedBestKey === row.sortKey ? null : row.sortKey) } : undefined}
                  style={{ cursor: extraBookies.length > 0 ? 'pointer' : 'default' }}
                >
                  <img src={BOOKMAKER_LOGOS[firstBookie]} alt={firstBookie} title={firstBookie} style={{ width: '18px', height: '18px', borderRadius: '4px', objectFit: 'contain' }} />
                  {extraBookies.length > 0 && (
                    <span style={{
                      position: 'absolute', top: '-3px', right: '-4px',
                      background: expandedBestKey === row.sortKey ? '#3b4f68' : '#2d3f55',
                      color: expandedBestKey === row.sortKey ? '#cbd5e1' : '#94a3b8',
                      fontSize: '6px', fontWeight: '700',
                      borderRadius: '3px', padding: '1px 2px', lineHeight: 1.3,
                    }}>
                      +{extraBookies.length}
                    </span>
                  )}
                  {expandedBestKey === row.sortKey && (
                    <span style={{
                      position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#1e293b', border: '1px solid #334155',
                      borderRadius: '6px', padding: '5px 7px',
                      display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                      zIndex: 200, whiteSpace: 'nowrap',
                    }}>
                      {extraBookies.map(bk => BOOKMAKER_LOGOS[bk]
                        ? <img key={bk} src={BOOKMAKER_LOGOS[bk]} alt={bk} title={bk} style={{ width: '18px', height: '18px', borderRadius: '4px', objectFit: 'contain' }} />
                        : <span key={bk} style={{ fontSize: '10px', color: '#94a3b8' }}>{bk}</span>
                      )}
                    </span>
                  )}
                </span>
              )}
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <span style={{ fontSize: '11.5px', fontWeight: '700', color: '#b0bec9' }}>{convertOdds(bestPrice, oddsFormat)}</span>
                {(() => {
                  const liq = formatLiquidity(bestSource?.outcome?.liquidity)
                  return liq ? <span style={{
                    position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                    fontSize: '7.5px', fontWeight: 600, lineHeight: 1,
                    color: '#64748b', whiteSpace: 'nowrap', pointerEvents: 'none',
                  }}>{liq}</span> : null
                })()}
              </span>
            </div>
          ) : <span style={{ color: '#2d3f55' }}>—</span>}
        </td>
        {visibleBookmakers.map((bk, bkIdx) => {
          const price = row.prices[bk]
          const isBest = price && price === bestPrice
          const blurCell = blurRow ? false : bkIdx >= blurFromCol
          const bkSource = row.sources?.[bk]
          const canTrack = !!price && !blurCell && !blurRow && !isPreview && !!bkSource
          // Keyed through the same adapter the slip stores from, so a cell and
          // the slip cannot disagree about what is selected.
          const inSlip = canTrack && slipKeys.has(cellSlipKey(game, bkSource, bk, price))
          return (
            <td
              key={bk}
              className={canTrack ? 'odds-track-cell' + (inSlip ? ' odds-track-cell--on' : '') : undefined}
              onClick={canTrack ? () => toggleOutcome(game, row, bk, price) : undefined}
              style={{
              padding: '8px 3px', textAlign: 'center', fontSize: '11.5px', fontWeight: '700',
              color: isBest ? '#22c55e' : (price ? '#b0bec9' : '#2d3f55'),
              borderBottom: '1px solid #253347', borderRight: '1px solid #253347',
              backgroundColor: isBest ? 'rgba(34,197,94,0.06)' : undefined,
              filter: blurCell ? 'blur(5px)' : 'none',
              userSelect: blurCell ? 'none' : 'auto',
            }}>
              {price ? (() => {
                // Exchanges only - dollars resting at this price. See the
                // EXCHANGE group in config/bookmakers.js; bookmakers send no
                // liquidity and render exactly as before. Absolutely positioned
                // so the row keeps its height (see the desktop note).
                const liq = formatLiquidity(bkSource?.outcome?.liquidity)
                return liq ? (
                  <span style={{ position: 'relative', display: 'inline-block' }}>
                    {convertOdds(price, oddsFormat)}
                    <span style={{
                      position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                      fontSize: '7.5px', fontWeight: 600, lineHeight: 1,
                      color: '#64748b', whiteSpace: 'nowrap', pointerEvents: 'none',
                    }}>{liq}</span>
                  </span>
                ) : convertOdds(price, oddsFormat)
              })() : '—'}
            </td>
          )
        })}
      </tr>
    )
  }

  const marketTree = useMemo(
    () => buildMarketTree(SPORT_AVAILABLE_MARKETS[selectedSport] || []),
    [selectedSport]
  )
  const draftBaseEntry = draftGroup ? marketTree[draftGroup]?.[draftBaseKey] : null

  // Opening the panel starts a fresh drill from the top, but the crumbs show
  // where the current selection sits so it never feels like a reset.
  const openSelector = () => {
    setStep('sport')
    setDraftCategory(LEAGUE_TO_GROUP[selectedSport] || null)
    setDraftGroup(null)
    setDraftBaseKey(null)
    setDropdownOpen(p => !p)
  }

  if (!isVisible) return null

  const dropdownLabel = selectedSport && selectedMarketLabel
    ? `${leagueLabel(selectedSport)} · ${selectedMarketLabel}`
    : selectedSport
      ? leagueLabel(selectedSport)
      : 'Sport / Market'

  return (
    <div
      className="odds-mobile-page"
      style={{ width: '100%', margin: 0, padding: 0, backgroundColor: '#010714', minHeight: '100vh', boxSizing: 'border-box' }}
      onClick={() => { setDropdownOpen(false); setExpandedBestKey(null) }}
    >
      <style>{`
        .odds-m-filter-btn {
          border: 1px solid #1b2538;
          border-radius: 8px;
          padding: 6px 10px;
          background: transparent;
          color: #cbd5e1;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
          font-size: 12px;
          font-weight: 500;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          transition: all 0.15s ease;
          height: 36px;
          max-width: 130px;
          overflow: hidden;
        }
        .odds-m-filter-btn span:first-child {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        /* Touch has no hover, so :active carries the feedback here. */
        .odds-m-filter-btn:hover,
        .odds-m-filter-btn:active {
          background: rgba(255, 255, 255, 0.04);
          border-color: #35486b;
        }
        .odds-m-filter-btn.active {
          background: rgba(59, 130, 246, 0.12);
          border-color: #3b82f6;
          color: #93c5fd;
        }
        .odds-m-arrow {
          font-size: 10px;
          opacity: 0.8;
          flex-shrink: 0;
        }
        .odds-m-dd-panel {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          width: calc(100vw - 20px);
          z-index: 2000;
          background: rgba(12,19,36,0.86);
          backdrop-filter: blur(22px) saturate(160%);
          -webkit-backdrop-filter: blur(22px) saturate(160%);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          box-shadow: 0 24px 56px rgba(0,0,0,0.60), inset 0 1px 0 rgba(255,255,255,0.07);
          max-height: calc(100vh - 160px);
          overflow: hidden;
        }
        /* A drill row is a whole tap target, so it runs the full width with the
           label left and the chevron right, and is taller than the desktop
           equivalent — this is a thumb, not a cursor. Padding is identical in
           every state so the label cannot shift as you move down the list. */
        .odds-m-dd-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 11px 12px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13.5px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #c3d0de;
          font-weight: 400;
          transition: background 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;
        }
        .odds-m-dd-item > span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .odds-m-dd-item:active {
          background: rgba(255,255,255,0.07);
        }
        .odds-m-dd-item.selected {
          color: #f1f5f9;
          font-weight: 600;
          background: linear-gradient(90deg,
            rgba(59,130,246,0.30) 0%,
            rgba(59,130,246,0.11) 58%,
            rgba(59,130,246,0.03) 100%);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.10),
            inset 0 0 0 1px rgba(96,165,250,0.22);
        }

        /* Multi-select row inside the filter modals. Same lit surface as the
           drill rows and the desktop dropdown — the highlight is the state.
           Taller than desktop because it is a thumb target. */
        .odds-m-check {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          color: #8397ad;
          font-weight: 400;
          transition: background 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;
        }
        .odds-m-check > span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .odds-m-check:active { background: rgba(255,255,255,0.07); }
        .odds-m-check.checked {
          color: #f1f5f9;
          font-weight: 500;
          background: linear-gradient(90deg,
            rgba(59,130,246,0.30) 0%,
            rgba(59,130,246,0.11) 58%,
            rgba(59,130,246,0.03) 100%);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.10),
            inset 0 0 0 1px rgba(96,165,250,0.22);
        }

        .odds-m-search-input {
          width: 100%;
          box-sizing: border-box;
          padding: 9px 11px;
          margin-bottom: 10px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.10);
          background: rgba(255,255,255,0.04);
          color: #e2e8f0;
          font-size: 13px;
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .odds-m-search-input::placeholder { color: #64748b; }
        .odds-m-search-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59,130,246,0.18);
        }

        /* ── Filter modals ──────────────────────────────────────────────────
           One shell for all four (Tournaments / Games / Players / Bookmakers);
           each used to carry its own inline slate-blue panel. Glass, to match
           the selector panel above it. */
        .odds-m-modal-backdrop {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.62);
          backdrop-filter: blur(3px);
          -webkit-backdrop-filter: blur(3px);
          z-index: 3000;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding-top: 60px;
        }
        .odds-m-modal {
          width: 90%;
          max-width: 400px;
          max-height: 80vh;
          overflow-y: auto;
          padding: 16px;
          border-radius: 14px;
          background: rgba(12,19,36,0.92);
          backdrop-filter: blur(22px) saturate(160%);
          -webkit-backdrop-filter: blur(22px) saturate(160%);
          border: 1px solid rgba(255,255,255,0.08);
          box-shadow: 0 24px 56px rgba(0,0,0,0.60), inset 0 1px 0 rgba(255,255,255,0.07);
        }
        .odds-m-modal-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
        }
        .odds-m-modal-title {
          font-size: 15px;
          font-weight: 700;
          margin: 0;
          color: #f1f5f9;
          letter-spacing: -0.01em;
        }
        .odds-m-modal-close {
          background: transparent;
          border: none;
          color: #7f8ea6;
          font-size: 24px;
          cursor: pointer;
          padding: 0 4px;
          line-height: 1;
        }
        .odds-m-modal-tools {
          display: flex;
          gap: 6px;
          margin-bottom: 10px;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .odds-m-modal-list {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        /* ── Drill-down shell ───────────────────────────────────────────── */
        .odds-m-drill {
          display: flex;
          flex-direction: column;
          max-height: calc(100vh - 180px);
        }
        .odds-m-drill-head {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px 4px;
        }
        .odds-m-drill-back {
          flex-shrink: 0;
          width: 28px; height: 28px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.10);
          background: rgba(255,255,255,0.05);
          color: #cbd5e1;
          cursor: pointer;
          padding: 0;
        }
        .odds-m-drill-back:active {
          background: rgba(59,130,246,0.18);
        }
        /* Crumbs are the path taken, not a title: quiet, and each one returns
           to the step that set it. */
        .odds-m-crumbs {
          display: flex;
          align-items: center;
          gap: 5px;
          min-width: 0;
          overflow: hidden;
          font-size: 11px;
          color: #7f8ea6;
        }
        .odds-m-crumb {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          cursor: pointer;
        }
        .odds-m-crumb + .odds-m-crumb::before {
          content: '›';
          margin-right: 5px;
          opacity: 0.55;
        }
        .odds-m-crumb:active { color: #cbd5e1; }
        /* The list scrolls; the head and label above it stay put. */
        .odds-m-drill-list {
          overflow-y: auto;
          padding: 2px 6px 10px;
          -webkit-overflow-scrolling: touch;
        }
        /* The one heading left: which step you are on. The drill shows a single
           list at a time, so the rank-2/rank-3 headings that separated nested
           groups in the old two-column panel have nothing left to head. */
        .odds-m-column-label {
          font-size: 9px;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          padding: 10px 10px 4px;
        }
        .odds-m-dd-scroll {
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #334155 transparent;
        }
        .odds-m-dd-scroll::-webkit-scrollbar { width: 2px; }
        .odds-m-dd-scroll::-webkit-scrollbar-track { background: transparent; }
        .odds-m-dd-scroll::-webkit-scrollbar-thumb { background: #334155; border-radius: 2px; }
        .odds-m-utility-btn {
          padding: 5px 10px;
          /* Clear glass, the same as the mobile header buttons
             (.bcm-action-row .bc-btn in app-mobile.css). */
          background: rgba(148, 163, 184, 0.03);
          color: #cbd5e1;
          border: 1px solid rgba(148, 163, 184, 0.16);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          font-family: 'Geist', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          transition: all 0.15s ease;
        }
        .odds-m-utility-btn:active {
          background: rgba(148, 163, 184, 0.08);
          border-color: rgba(148, 163, 184, 0.3);
          color: #f1f5f9;
        }
        .odds-m-table-scroll {
          overflow: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: #2d3f55 transparent;
        }
        .odds-m-table-scroll::-webkit-scrollbar { width: 3px; height: 3px; }
        .odds-m-table-scroll::-webkit-scrollbar-track { background: transparent; }
        .odds-m-table-scroll::-webkit-scrollbar-thumb { background: #2d3f55; border-radius: 3px; }
        .odds-m-table-scroll::-webkit-scrollbar-corner { background: transparent; }
        .odds-m-table-scroll tbody tr:hover td { background-color: rgba(255,255,255,0.03); }
        .odds-m-logo-wrap { position: relative; display: inline-flex; flex-shrink: 0; }

          /* Player / team band image. 24px against the band's 12px label - a
             touch smaller than desktop's 28px, because the whole card is about
             310px wide and the band also carries an "N lines" pill.
             Only on the BAND, never on a price row: that label column is
             sticky, 11.5px and as narrow as the table allows, so an image there
             would push the name into wrapping.
             The frosted ring is stripped for the same reason desktop strips it
             - a flat disc reads cleaner at this size - leaving the circular
             crop for faces and a bare mark for crests. */
          /* Fixture header. 26px, and the PAIR inside it is sized by the same
             rule desktop uses: members must satisfy width + the second one's
             left = the slot width, or the pair spills and shunts the fixture
             name. 16 at left 10 overlaps by 6px (~38%). Kept low deliberately -
             .bc-lm-logo-img is object-fit: contain, so a WIDE mark (an MLB
             wordmark) fills only a thin band of its box and two of those at a
             nominal 50% sit almost on top of each other. */
          .odds-m-game-subject { display: inline-flex; flex-shrink: 0; line-height: 0; }
          .odds-m-game-subject .bc-lm-avatar-wrap,
          .odds-m-game-subject .bc-lm-logo-wrap,
          .odds-m-game-subject .bc-lm-logo-pair { width: 26px; height: 26px; transform: none; }
          .odds-m-game-subject .bc-lm-avatar-wrap { background: #1e2d45; border: none; box-shadow: none; backdrop-filter: none; -webkit-backdrop-filter: none; }
          .odds-m-game-subject .bc-lm-avatar-wrap::after { display: none; }
          .odds-m-game-subject > .bc-lm-logo-wrap .bc-lm-logo-img { inset: 0; margin: auto; width: 88%; height: 88%; }
          .odds-m-game-subject .bc-lm-logo-pair .bc-lm-logo-wrap { position: absolute; top: 5px; left: 0; width: 16px; height: 16px; }
          .odds-m-game-subject .bc-lm-logo-pair .bc-lm-logo-wrap:last-child { left: 10px; }
          .odds-m-game-subject .bc-ev-glyph { position: absolute; inset: 0; width: 100%; height: 100%; padding: 2px; box-sizing: border-box; color: #46566f; }
          .odds-m-game-subject .bc-ev-glyph-person { padding: 1px 1px 0; color: #3f4f66; }

          .odds-m-row-subject { display: inline-flex; flex-shrink: 0; line-height: 0; }
          .odds-m-row-subject .bc-lm-avatar-wrap,
          .odds-m-row-subject .bc-lm-logo-wrap { width: 18px; height: 18px; transform: none; }
          .odds-m-row-subject .bc-lm-avatar-wrap { background: #1e2d45; border: none; box-shadow: none; backdrop-filter: none; -webkit-backdrop-filter: none; }
          .odds-m-row-subject .bc-lm-avatar-wrap::after { display: none; }
          .odds-m-row-subject > .bc-lm-logo-wrap .bc-lm-logo-img { inset: 0; margin: auto; width: 90%; height: 90%; }
          .odds-m-row-subject .bc-ev-glyph { position: absolute; inset: 0; width: 100%; height: 100%; padding: 1px; box-sizing: border-box; color: #46566f; }

          .odds-m-band-subject { display: inline-flex; flex-shrink: 0; line-height: 0; }
          .odds-m-band-subject .bc-lm-avatar-wrap,
          .odds-m-band-subject .bc-lm-logo-wrap { width: 24px; height: 24px; transform: none; }
          .odds-m-band-subject .bc-lm-avatar-wrap { background: #1e2d45; border: none; box-shadow: none; backdrop-filter: none; -webkit-backdrop-filter: none; }
          .odds-m-band-subject .bc-lm-avatar-wrap::after { display: none; }
          .odds-m-band-subject > .bc-lm-logo-wrap .bc-lm-logo-img { inset: 0; margin: auto; width: 88%; height: 88%; }
          .odds-m-band-subject .bc-ev-glyph { position: absolute; inset: 0; width: 100%; height: 100%; padding: 2px; box-sizing: border-box; color: #46566f; }
          .odds-m-band-subject .bc-ev-glyph-person { padding: 1px 1px 0; color: #3f4f66; }
      `}</style>

      <div style={{ backgroundColor: 'transparent', borderRadius: 0, borderBottom: '1px solid #1b2538', padding: '10px 12px', marginBottom: 0 }}>

        {/* Filter + Controls Row */}
        <div className="bcm-action-row">
          <div className="bcm-action-left">
            <FilterDropdown
              isOpen={dropdownOpen}
              onToggle={openSelector}
              buttonContent={dropdownLabel}
              isActive={!!(selectedSport && selectedMarketLabel)}
            >
              <div className="odds-m-drill">
                {/* Breadcrumb + back. Each crumb returns to the step that set
                    it, so correcting a wrong turn is one tap rather than a
                    close-and-start-again. */}
                <div className="odds-m-drill-head">
                  {step !== 'sport' && (
                    <button
                      className="odds-m-drill-back"
                      onClick={() => setStep(
                        step === 'period' ? 'market'
                          : step === 'market' ? 'group'
                          // Mirror the skip going forward, or back lands on the
                          // one-row League list the tap just bypassed.
                          : step === 'group' ? ((ODDS_HIERARCHY[draftCategory]?.leagues.length || 0) === 1 ? 'sport' : 'league')
                          : 'sport'
                      )}
                      aria-label="Back"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </button>
                  )}
                  <div className="odds-m-crumbs">
                    {draftCategory && (
                      <span className="odds-m-crumb" onClick={() => setStep('league')}>{draftCategory}</span>
                    )}
                    {step !== 'sport' && step !== 'league' && (
                      <span
                        className="odds-m-crumb"
                        onClick={() => setStep((ODDS_HIERARCHY[draftCategory]?.leagues.length || 0) === 1 ? 'sport' : 'league')}
                      >
                        {leagueLabel(selectedSport)}
                      </span>
                    )}
                    {draftGroup && (step === 'market' || step === 'period') && (
                      <span className="odds-m-crumb" onClick={() => setStep('market')}>{draftGroup}</span>
                    )}
                    {draftBaseEntry && step === 'period' && (
                      <span className="odds-m-crumb">{draftBaseEntry.label}</span>
                    )}
                  </div>
                </div>

                <div className="odds-m-column-label">
                  {step === 'sport' ? 'Sport' : step === 'league' ? 'League'
                    : step === 'group' ? 'Market group' : step === 'market' ? 'Market'
                    // Futures fill the last step with options (Top 5, Round 6), not periods.
                    : isFuturesLeague(selectedSport) ? 'Option' : 'Period'}
                </div>

                <div className="odds-m-dd-scroll odds-m-drill-list">
                  {step === 'sport' && Object.keys(ODDS_HIERARCHY).map(category => (
                    <div
                      key={category}
                      className={`odds-m-dd-item${draftCategory === category ? ' selected' : ''}`}
                      onClick={() => {
                        setDraftCategory(category)
                        // A category with one league (Baseball, American
                        // Football, Hockey) has no decision to offer, so skip
                        // straight past it rather than showing a one-row step.
                        const { leagues } = ODDS_HIERARCHY[category]
                        if (leagues.length === 1) {
                          handleSportSelect(leagues[0])
                          setDraftGroup(null); setDraftBaseKey(null)
                          setStep('group')
                        } else {
                          setStep('league')
                        }
                      }}
                    >
                      <span>{category}</span>
                      <ChevronRight />
                    </div>
                  ))}

                  {step === 'league' && (ODDS_HIERARCHY[draftCategory]?.leagues || []).map(id => (
                    <div
                      key={id}
                      className={`odds-m-dd-item${selectedSport === id ? ' selected' : ''}`}
                      onClick={() => {
                        handleSportSelect(id)
                        setDraftBaseKey(null)
                        // Skip a step that offers one option. A futures league's
                        // markets are all outrights, so Group holds a single row
                        // and the user would tap it only to move on.
                        //
                        // Built from the CLICKED id, not marketTree — that reads
                        // selectedSport, which this same handler has only just
                        // set and which does not update until the next render.
                        const groups = Object.keys(buildMarketTree(SPORT_AVAILABLE_MARKETS[id] || []))
                        if (groups.length === 1) { setDraftGroup(groups[0]); setStep('market') }
                        else { setDraftGroup(null); setStep('group') }
                      }}
                    >
                      <span>{leagueLabel(id)}</span>
                      <ChevronRight />
                    </div>
                  ))}

                  {step === 'group' && Object.keys(marketTree).map(group => (
                    <div
                      key={group}
                      className={`odds-m-dd-item${draftGroup === group ? ' selected' : ''}`}
                      onClick={() => { setDraftGroup(group); setDraftBaseKey(null); setStep('market') }}
                    >
                      <span>{group}</span>
                      <ChevronRight />
                    </div>
                  ))}

                  {step === 'market' && Object.entries(marketTree[draftGroup] || {}).map(([baseKey, entry]) => {
                    const periods = orderPeriods(entry.periods)
                    return (
                      <div
                        key={baseKey}
                        className={`odds-m-dd-item${draftBaseKey === baseKey ? ' selected' : ''}`}
                        onClick={() => {
                          setDraftBaseKey(baseKey)
                          // Nothing to choose when a market has one period, so
                          // commit rather than showing a one-item step.
                          if (periods.length === 1) handleMarketSelect(entry.periods[periods[0]])
                          else setStep('period')
                        }}
                      >
                        <span>{entry.label}</span>
                        {periods.length > 1 && <ChevronRight />}
                      </div>
                    )
                  })}

                  {step === 'period' && draftBaseEntry && orderPeriods(draftBaseEntry.periods).map(period => {
                    const label = draftBaseEntry.periods[period]
                    return (
                      <div
                        key={period}
                        className={`odds-m-dd-item${selectedMarketLabel === label ? ' selected' : ''}`}
                        onClick={() => handleMarketSelect(label)}
                      >
                        <span>{period}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </FilterDropdown>

          </div>

          <div className="bcm-action-right">
            <button
              className={`bc-btn bc-btn-toggle${autoRefresh ? ' toggle-active' : ''}`}
              onClick={() => setAutoRefresh(p => !p)}
            >
              Auto
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
            <button className="bc-btn bc-icon-btn" onClick={() => fetchOdds(selectedSport, selectedMarketKeys)} disabled={loading}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Filter chips row */}
        {groupedGames.length > 0 && (
          <div className="bcm-filter-row">
            {/* Only when the tournament says more than the league row did —
                several loaded, or a single one the league label does not name.
                Mirrors the desktop condition exactly. */}
            {(allTournaments.length > 1 || (allTournaments.length === 1 && allTournaments[0] !== leagueLabel(selectedSport))) && (
              <button className="bc-btn" onClick={() => setTournamentsModalOpen(true)}>
                {allTournaments.length === 1
                  ? allTournaments[0]
                  : `Tournaments${hiddenTournaments.size ? ` (${allTournaments.length - hiddenTournaments.size}/${allTournaments.length})` : ''}`}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
            )}
            {/* Futures has no games: each row group is a MARKET, and only one is
                selected at a time, so the list holds a single unfilterable entry. */}
            {!isFuturesLeague(selectedSport) && (
              <button className="bc-btn" onClick={() => setGamesModalOpen(true)}>
                {`Games${hiddenGames.size ? ` (${visibleGames.length}/${groupedGames.length})` : ''}`}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
            )}
            {/* Round picker - only for markets split per round (Brownlow 3 Votes). */}
            {availableRounds.length > 0 && (
              <button className="bc-btn" onClick={() => setRoundModalOpen(true)}>
                {effectiveRound != null ? roundLabel(effectiveRound) : 'Round'}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
            )}
            {/* Hidden where the players ARE the competitors (tennis): the Games
                list already names both, one row per match instead of two. */}
            {allPlayers.length > 0 && !playersAreCompetitors(selectedSport) && (
              <button className="bc-btn" onClick={() => setPlayersModalOpen(true)}>
                {`Players${hiddenPlayers.size ? ` (${allPlayers.length - hiddenPlayers.size}/${allPlayers.length})` : ''}`}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
            )}
            <button className="bc-btn" onClick={() => setBookmakerModalOpen(true)}>
              Bookmakers
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
          </div>
        )}

        {/* States */}
        {error && !loading && groupedGames.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 24px', color: '#475569', textAlign: 'center', gap: '10px' }}>
            <div style={{ fontSize: '32px', opacity: 0.5 }}>⚠️</div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>Error loading odds</div>
            <div style={{ fontSize: '12px', color: '#475569' }}>{error}</div>
          </div>
        )}
        {!loading && !error && selectedMarketLabel && groupedGames.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 24px', color: '#475569', textAlign: 'center', gap: '10px' }}>
            <div style={{ fontSize: '32px', opacity: 0.5 }}>🔍</div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>No upcoming games found</div>
            <div style={{ fontSize: '12px', color: '#475569' }}>Try a different sport or market</div>
          </div>
        )}

        {/* Table — bleeds to card edges like desktop */}
        {groupedGames.length > 0 && (
          <div style={{ position: 'relative', margin: '10px -12px -10px', borderTop: '1px solid #334155', overflow: 'hidden' }}>
            <div
              ref={tableWrapperRef}
              className="odds-m-table-scroll"
              style={{ overflow: isPreview ? 'hidden' : 'auto', height: isPreview ? 360 : tableHeight }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#0a1020' }}>
                  <tr>
                    <th style={{
                      padding: '9px 8px', textAlign: 'center', fontSize: '10px', fontWeight: '700',
                      color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em',
                      borderBottom: '1px solid #334155', borderRight: '1px solid #253347',
                      minWidth: '110px', position: 'sticky', left: 0, zIndex: 4, backgroundColor: '#0a1020',
                    }}>
                      Outcome
                    </th>
                    <th
                      style={{
                        padding: '9px 4px', textAlign: 'center', fontSize: '10px', fontWeight: '700',
                        color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em',
                        borderBottom: '1px solid #334155', borderRight: '2px solid #334155',
                        minWidth: '56px', position: 'sticky', zIndex: 2, backgroundColor: '#0a1020',
                        cursor: oddsSortable ? 'pointer' : 'default', userSelect: 'none',
                      }}
                      onClick={oddsSortable ? () => setBestSort(s => s === null ? 'desc' : s === 'desc' ? 'alpha' : null) : undefined}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                        Best
                        {oddsSortable && (
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: bestSort === 'alpha' ? 0.4 : 1 }}>
                            {bestSort === null
                              ? <polyline points="18 15 12 9 6 15"/>
                              : bestSort === 'desc'
                                ? <polyline points="6 9 12 15 18 9"/>
                                : <><polyline points="6 9 12 4 18 9"/><polyline points="6 15 12 20 18 15"/></>}
                          </svg>
                        )}
                      </span>
                    </th>
                    {visibleBookmakers.map(bk => (
                      <th key={bk} style={{
                        padding: '9px 3px', textAlign: 'center',
                        borderBottom: '1px solid #334155', borderRight: '1px solid #253347',
                        minWidth: '44px', position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#0a1020',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          {BOOKMAKER_LOGOS[bk] ? (
                            <img src={BOOKMAKER_LOGOS[bk]} alt={bk} title={bk} style={{ width: '22px', height: '22px', borderRadius: '5px', objectFit: 'contain' }} />
                          ) : (
                            <span style={{ fontSize: '8px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{bk}</span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleGames.map((game, idx) => {
                    if (isPreview && idx >= 3) return null
                    const outcomeRows = getOutcomeRows(game)
                    if (outcomeRows.length === 0) return null
                    const gameId = `${game.game.home_team}_${game.game.away_team}_${game.game.commence_time}`
                    const blurGame = isPreview && idx > 0
                    const lineGroups = getLineGroups(outcomeRows) || getSpreadGroup(outcomeRows)
                    return (
                      <React.Fragment key={idx}>
                        {/* Game header row */}
                        <tr style={{ filter: blurGame ? 'blur(5px)' : 'none', pointerEvents: blurGame ? 'none' : 'auto' }}>
                          <td
                            colSpan={2 + visibleBookmakers.length}
                            style={{ padding: 0 }}
                          >
                            <div style={{
                              padding: '7px 10px', backgroundColor: '#0a1020',
                              borderTop: '2px solid #1b2538', borderBottom: '1px solid #1b2538',
                              display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
                            }}>
                              {/* Both crests on the FIXTURE HEADER - one image per
                                  game rather than one per price row, on the line
                                  the eye uses to jump between games. A futures
                                  header has no two teams and falls back to the
                                  sport glyph, same as desktop. */}
                              <span className="odds-m-game-subject">
                                <SubjectImage opp={{
                                  home_team: game.game.home_team,
                                  away_team: game.game.away_team,
                                  sport: game.game.sport
                                }} />
                              </span>
                              <span style={{ fontSize: '12px', fontWeight: '600', color: '#e2e8f0' }}>
                                {/* Futures name the MARKET here — no two teams, no start time. */}
                                {game.game.futuresTitle || `${game.game.away_team} vs ${game.game.home_team}`}
                              </span>
                              {game.game.commence_time && <>
                                <span style={{ fontSize: '11px', color: '#475569' }}>·</span>
                                <span style={{ fontSize: '11px', color: '#64748b' }}>{formatTime(game.game.commence_time)}</span>
                              </>}
                            </div>
                          </td>
                        </tr>

                        {lineGroups ? (
                          lineGroups.map((pg, pgIdx) => {
                            const pKey = `${gameId}_${pg.label}`
                            const expanded = expandedPlayers.has(pKey)
                            const mainLine = pg.lines.find(l => l.point === pg.mainPoint) || pg.lines[0]
                            const otherLines = pg.lines.filter(l => l !== mainLine)
                            const blurRow = blurGame || (isPreview && idx === 0 && pgIdx >= 2)
                            const lineLabel = (row) => row.label.includes(' — ') ? row.label.split(' — ').slice(1).join(' — ') : row.label
                            return (
                              <React.Fragment key={pKey}>
                                <tr style={{ filter: blurRow ? 'blur(5px)' : 'none', pointerEvents: blurRow ? 'none' : 'auto' }}>
                                  <td colSpan={2 + visibleBookmakers.length}
                                      style={{ padding: '8px 10px', background: '#19233490', borderTop: '1px solid #2d3f55', borderBottom: '1px solid #253347', cursor: otherLines.length ? 'pointer' : 'default' }}
                                      onClick={() => otherLines.length && togglePlayer(pKey)}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      {/* Only a band we can NAME gets an image: a player
                                          gets their headshot, a team total the club crest,
                                          matched against the fixture (or, on futures, the
                                          clubs that market prices) so no bogus URL is
                                          requested. A game total is neither and gets
                                          nothing rather than an empty box.
                                          The BAND is where this belongs on mobile - it
                                          spans the full table width, unlike the 11.5px
                                          sticky label column, which has no room for one. */}
                                      {(() => {
                                        const isPlayer = playerNameSet.has(pg.label)
                                        const isTeam = !isPlayer && (
                                          pg.label === game.game.home_team ||
                                          pg.label === game.game.away_team ||
                                          !!game.game.futuresTeams?.has(pg.label)
                                        )
                                        if (!isPlayer && !isTeam) return null
                                        return (
                                          <span className="odds-m-band-subject">
                                            <SubjectImage opp={{
                                              player_name: isPlayer ? pg.label : null,
                                              team_name: isTeam ? pg.label : null,
                                              sport: game.game.sport
                                            }} />
                                          </span>
                                        )
                                      })()}
                                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#e2e8f0' }}>{pg.label}</span>
                                      {otherLines.length > 0 && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 700, color: '#a78bfa', background: 'rgba(124,58,237,0.13)', padding: '2px 7px', borderRadius: '999px' }}>
                                          {pg.lines.length} lines
                                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                                               style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}>
                                            <polyline points="6 9 12 15 18 9"/>
                                          </svg>
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {mainLine.rows.map(row => renderOddsRow(game, gameId, row, { label: lineLabel(row), blurRow, indent: true }))}
                                {expanded && otherLines.map(l => l.rows.map(row => renderOddsRow(game, gameId, row, { label: lineLabel(row), blurRow, indent: true })))}
                              </React.Fragment>
                            )
                          })
                        ) : (
                          // Flat markets — when the Best sort is active on a tryscorer market,
                          // order this game's rows by best price (asc/desc); else default order.
                          (oddsSortable && bestSort !== 'alpha'
                            ? [...outcomeRows].sort((a, b) => {
                                // null = shortest price first (the default). Unpriced rows
                                // sink either way, so they never sit among the favourites.
                                const asc = bestSort === null
                                const pa = bestOddsMap[`${gameId}_${a.sortKey}`] ?? Infinity
                                const pb = bestOddsMap[`${gameId}_${b.sortKey}`] ?? Infinity
                                if (pa === Infinity || pb === Infinity) return pa - pb
                                return asc ? pa - pb : pb - pa
                              })
                            : outcomeRows
                          ).map((row, rowIdx) => {
                            const blurRow = blurGame || (isPreview && idx === 0 && rowIdx >= 2)
                            const blurFromCol = (isPreview && idx === 0 && rowIdx < 2) ? 3 : Infinity
                            return renderOddsRow(game, gameId, row, { blurRow, blurFromCol, showSubject: true })
                          })
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Preview fade */}
            {isPreview && (
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '100px', background: 'linear-gradient(to bottom, transparent, #010714)', pointerEvents: 'none' }} />
            )}
          </div>
        )}

      </div>{/* end header card */}

      {/* Preview CTA (app-style PreviewWall) — sits on the page bg, like other tool pages */}
      {isPreview && (
        <div style={{ paddingTop: '16px' }}>
          <PreviewWall
            title="Compare odds across every bookmaker"
            features={['Best odds highlighted across all bookmakers', 'All sports, markets and alternate lines', 'Real time refresh']}
            planName="Premium"
            accent="#60a5fa"
            hadTrial={hadTrial}
          />
        </div>
      )}

      {/* Tournaments Modal */}
      {tournamentsModalOpen && (
        <div className="odds-m-modal-backdrop" onClick={() => setTournamentsModalOpen(false)}>
          <div className="odds-m-modal" onClick={e => e.stopPropagation()}>
            <div className="odds-m-modal-head">
              <h3 className="odds-m-modal-title">Tournaments</h3>
              <button onClick={() => setTournamentsModalOpen(false)} className="odds-m-modal-close">×</button>
            </div>
            <div className="odds-m-modal-tools">
              <button onClick={() => setHiddenTournaments(new Set())} className="odds-m-utility-btn">Select All</button>
              <button onClick={() => setHiddenTournaments(new Set(allTournaments))} className="odds-m-utility-btn">Clear All</button>
            </div>
            <div className="odds-m-modal-list">
              {allTournaments.map(t => (
                <CheckboxItem key={t} item={t} selected={!hiddenTournaments.has(t)} onToggle={() => toggleTournament(t)} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Games Modal */}
      {gamesModalOpen && (
        <div
          className="odds-m-modal-backdrop"
          onClick={() => setGamesModalOpen(false)}
        >
          <div
            className="odds-m-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="odds-m-modal-head">
              <h3 className="odds-m-modal-title">Games</h3>
              <button onClick={() => setGamesModalOpen(false)} className="odds-m-modal-close">×</button>
            </div>
            <input
              value={gameSearch}
              onChange={e => setGameSearch(e.target.value)}
              placeholder="Search games…"
              className="odds-m-search-input"
            />
            <div className="odds-m-modal-tools">
              <button onClick={() => setHiddenGames(new Set())} className="odds-m-utility-btn">Select All</button>
              <button onClick={() => setHiddenGames(new Set(groupedGames.map(gameKey)))} className="odds-m-utility-btn">Clear All</button>
            </div>
            <div className="odds-m-modal-list">
              {groupedGames
                .filter(g => gameLabel(g).toLowerCase().includes(gameSearch.toLowerCase()))
                .map(g => {
                  const key = gameKey(g)
                  return <CheckboxItem key={key} item={gameLabel(g)} selected={!hiddenGames.has(key)} onToggle={() => toggleGame(key)} />
                })}
            </div>
          </div>
        </div>
      )}

      {/* Players Modal */}
      {roundModalOpen && (
        <div className="odds-m-modal-backdrop" onClick={() => setRoundModalOpen(false)}>
          <div className="odds-m-modal" onClick={e => e.stopPropagation()}>
            <div className="odds-m-modal-head">
              <h3 className="odds-m-modal-title">Round</h3>
              <button onClick={() => setRoundModalOpen(false)} className="odds-m-modal-close">×</button>
            </div>
            <div className="odds-m-modal-list">
              {availableRounds.map(r => (
                <div
                  key={r}
                  onClick={() => { setSelectedRound(r); setRoundModalOpen(false) }}
                  className={`odds-m-dd-item${effectiveRound === r ? ' selected' : ''}`}
                >
                  {roundLabel(r)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {playersModalOpen && (
        <div
          className="odds-m-modal-backdrop"
          onClick={() => setPlayersModalOpen(false)}
        >
          <div
            className="odds-m-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="odds-m-modal-head">
              <h3 className="odds-m-modal-title">Players</h3>
              <button onClick={() => setPlayersModalOpen(false)} className="odds-m-modal-close">×</button>
            </div>
            <input
              value={playerSearch}
              onChange={e => setPlayerSearch(e.target.value)}
              placeholder="Search players…"
              className="odds-m-search-input"
            />
            <div className="odds-m-modal-tools">
              <button onClick={() => setHiddenPlayers(new Set())} className="odds-m-utility-btn">Select All</button>
              <button onClick={() => setHiddenPlayers(new Set(allPlayers))} className="odds-m-utility-btn">Clear All</button>
            </div>
            <div className="odds-m-modal-list">
              {allPlayers
                .filter(p => p.toLowerCase().includes(playerSearch.toLowerCase()))
                .map(p => (
                  <CheckboxItem key={p} item={p} selected={!hiddenPlayers.has(p)} onToggle={() => togglePlayerFilter(p)} />
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Bookmakers Modal */}
      {bookmakerModalOpen && (
        <div
          className="odds-m-modal-backdrop"
          onClick={() => setBookmakerModalOpen(false)}
        >
          <div
            className="odds-m-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="odds-m-modal-head">
              <h3 className="odds-m-modal-title">Bookmakers</h3>
              <button onClick={() => setBookmakerModalOpen(false)} className="odds-m-modal-close">×</button>
            </div>
            <div className="odds-m-modal-tools">
              <button onClick={() => setSelectedBookmakers(new Set(ALL_BOOKMAKERS))} className="odds-m-utility-btn">Select All</button>
              <button onClick={() => setSelectedBookmakers(new Set())} className="odds-m-utility-btn">Clear All</button>
            </div>
            {Object.entries(BOOKMAKERS).map(([country, bookmakers]) => (
              <div key={country} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ padding: '3px 8px', backgroundColor: '#334155', border: '1px solid #475569', color: '#f1f5f9', fontSize: '10px', borderRadius: '4px', fontWeight: '600', letterSpacing: '0.03em' }}>{country}</span>
                    {/* Exchange prices here are net of the taker fee, so they read
                        LOWER than on the exchange's own site - explain it where the
                        group is named. */}
                    {country === 'EXCHANGE' && <ExchangeFeeInfo />}
                  </span>
                  <button onClick={() => toggleCountryBookmakers(country)} className="odds-m-utility-btn">
                    {bookmakers.every(bk => selectedBookmakers.has(bk)) ? 'Deselect' : 'Select'} All
                  </button>
                </div>
                <div className="odds-m-modal-list">
                  {bookmakers.map(bk => (
                    <CheckboxItem key={bk} item={bk} selected={selectedBookmakers.has(bk)} onToggle={toggleBookmakerSelection} logo={BOOKMAKER_LOGOS[bk]} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Same slip as desktop, laid out for a phone. Singles and multis are
          the same list submitted differently, and every rule, sum and save is
          shared, so a multi built here is the same object as one built on a
          desktop. */}
      <OddsBetslip
        legs={slipLegs}
        onRemove={removeFromSlip}
        onOddsChange={setLegOdds}
        onClear={() => setSlipLegs([])}
        onSaved={(mode, count) => setSlipToast(
          mode === 'multi' ? 'Multi added to tracker' : `${count} bet${count === 1 ? '' : 's'} added to tracker`
        )}
        compact
      />

      {slipToast && (
        <div
          onAnimationEnd={() => setSlipToast(null)}
          className="odds-slip-toast"
          style={{
            position: 'fixed', left: '12px', right: '12px', bottom: '76px', zIndex: 61,
            padding: '11px 16px', borderRadius: '10px', textAlign: 'center',
            border: '1px solid rgba(34,211,238,0.4)', backgroundColor: '#0b1322',
            color: '#67e8f9', fontSize: '12.5px', fontWeight: 700
          }}
        >
          {slipToast}
        </div>
      )}
    </div>
  )
}
